import React, { Fragment, useRef, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useHandleOpenViewerClick } from "../../../media-viewer/mediaViewerRedux";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { isBlank, notBlank } from "../../../util/ext";
import {
  isAnthologyView,
  isCollectionOrAnthologyView,
  isContentGroupTopLevelView,
  isModuleView,
  isUngroupedHome,
  notContent,
  useView,
} from "../organizeUtil";
import { useSelected } from "../OrganizeProvider";
import { getIds } from "../../../util/mediaUtil";
import {
  equals,
  includes,
  intersection,
  isEmpty,
  lensProp,
  map,
  set,
} from "ramda";
import { useMediaTable } from "../../common/use-mediaTable";

import Checkbox from "@material-ui/core/Checkbox";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";

import DraggableRow from "../files/DraggableRow";

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: "20px",
  },

  titleCell: {
    width: "30%",
  },

  includedInCell: {
    paddingLeft: "15px",
    width: "15%",
  },

  originCell: {
    width: "10%",
    paddingLeft: 20,
  },

  medCell: {
    width: "8%",
  },

  actionsCell: {
    width: "10%",
  },

  smallCell: {
    width: "5%",
  },

  container: {
    padding: 0,
    overflowY: "auto",
    maxHeight: 800,
  },

  empty: {
    textAlign: "center",
    marginTop: theme.spacing(8),
    fontStyle: "italic",
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },

  reviewTitle: {
    paddingLeft: theme.spacing(6),
  },

  table: {
    height: "100%",
    overflowY: "scroll",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const getEmptyTableText = view => {
  const notContentText = view => `This ${view} currently contains no content.`;

  if (notContent(view)) {
    return notContentText(view);
  } else if (view === "review") {
    return "No files for review.";
  } else if (view === "organize-ungrouped") {
    return "No unorganized files available.";
  } else {
    return "No files available.";
  }
};

const EmptyTableText = props => {
  const view = useView();
  const classes = useStyles();

  return <p className={classes.empty}>{getEmptyTableText(view)}</p>;
};

const ContentGroupFilesTable = props => {
  const {
    files,
    isLoading,
    onContentEditClick,
    onItemsDelete,
    onScrollVisible,
    onSortChange,
    onRowsPerPageChange,
    order,
    orderBy,
    parent,
    selectedContent,
    setLoading,
    searchState,
    sourceFilter,
  } = props;

  const classes = useStyles();
  const view = useView();
  const { SnackAlert } = useSnackAlert();

  const { handleRequestSort } = useMediaTable(
    {
      onSortChange,
      onRowsPerPageChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
    },
  );

  const { appendToSelectedContent } = useSelected();

  const { handleOpenViewerClick, isViewerLoading } = useHandleOpenViewerClick();

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(files)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, files]);

  //-- Manage Checkbox State
  const [checkbox, setCheckbox] = useState({
    checked: false,
    indeterminate: false,
  });
  useEffect(() => {
    const selectedIdsForPage = intersection(
      getIds(files),
      getIds(selectedContent),
    );
    if (!isEmpty(selectedIdsForPage)) {
      setCheckbox({
        checked: equals(selectedIdsForPage.length, files.length),
        indeterminate: !equals(selectedIdsForPage.length, files.length),
      });
    } else {
      setCheckbox({ checked: false, indeterminate: false });
    }
  }, [files, selectedContent]);

  const handleChange = event => {
    const updated = map(set(lensProp("parent"), parent), files);
    appendToSelectedContent(checkbox, updated);
  };

  return (
    <Paper elevation={0} className={classes.root}>
      {isBlank(files) && !isLoading ? (
        <EmptyTableText />
      ) : (
        <Fragment>
          <SnackAlert />
          <TableContainer ref={tableContainerEl} className={classes.container}>
            <Table stickyHeader aria-label="Content" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    align="center"
                    className={classes.smallCell}
                  >
                    <Checkbox
                      color="primary"
                      indeterminate={checkbox.indeterminate}
                      checked={checkbox.checked}
                      onChange={handleChange}
                      inputProps={{ "aria-label": "Select" }}
                    />
                  </TableCell>

                  <TableCell
                    key="content"
                    align="left"
                    className={classes.titleCell}
                  >
                    <TableSortLabel
                      active={includes(orderBy, ["title", "ct.title"])}
                      direction={order}
                      onClick={() => {
                        const sort = isAnthologyView(view)
                          ? "ct.title"
                          : "title";
                        handleRequestSort(sort);
                      }}
                    >
                      Title
                      {orderBy === "title" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="date-updated"
                    align="left"
                    className={classes.medCell}
                  >
                    <TableSortLabel
                      active={includes(orderBy, [
                        "auditUpdated",
                        "m.auditUpdated",
                        "media.auditAdded",
                        "updated",
                      ])}
                      direction={order}
                      onClick={() => {
                        const sort = isCollectionOrAnthologyView(view)
                          ? "updated"
                          : isModuleView(view)
                          ? "m.auditUpdated"
                          : "media.auditAdded";
                        handleRequestSort(sort);
                      }}
                    >
                      {isContentGroupTopLevelView(view)
                        ? "Uploaded"
                        : "Updated"}
                      {includes(orderBy, [
                        "auditUpdated",
                        "m.auditUpdated",
                        "media.auditAdded",
                        "updated",
                      ]) ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="content-type"
                    align="left"
                    className={classes.medCell}
                  >
                    <TableSortLabel
                      active={orderBy === "type"}
                      direction={order}
                      onClick={() => handleRequestSort("type")}
                    >
                      Type
                      {orderBy === "type" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="origin"
                    align="left"
                    className={classes.originCell}
                  >
                    Origin
                  </TableCell>

                  {!isUngroupedHome(view) && (
                    <TableCell
                      key="included-in"
                      align="left"
                      className={classes.includedInCell}
                    >
                      Included In
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {files.map((content, index) => (
                  <DraggableRow
                    index={index}
                    loading={isViewerLoading}
                    key={content.id}
                    content={content}
                    parent={parent}
                    handleOpenViewerClick={handleOpenViewerClick}
                    onItemsDelete={onItemsDelete}
                    onContentEditClick={onContentEditClick}
                    setLoading={setLoading}
                    searchState={searchState}
                    sourceFilter={sourceFilter}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Fragment>
      )}
    </Paper>
  );
};

export default ContentGroupFilesTable;
